import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { PROJECTS, IMAGES } from "../data/site";

const FILTERS = ["Alle", "Wohnungsbau", "Bildung", "Gewerbe", "Kultur & Denkmal", "Forschung", "Sozialbau", "Neubau", "Infrastruktur"];

export default function Projekte() {
  const [filter, setFilter] = useState("Alle");
  const visible = filter === "Alle" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <main data-testid="projekte-page">
      <section className="relative overflow-hidden border-b border-line">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES.rohbau} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 pb-20 pt-40 md:px-12 md:pt-52">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Referenzen</p>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-bone md:text-8xl">
              16 Bauvorhaben.<br />Ein Handschlag.
            </h1>
            <p className="mt-10 max-w-xl text-base leading-relaxed text-ash md:text-lg">
              Von der Alten Oper Frankfurt über die GSI Darmstadt bis zum Geschosswohnungsbau in Weinheim — unsere Projekte sprechen für sich.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-16 md:px-12 md:py-24">
        <Reveal>
          <div className="flex flex-wrap gap-3" data-testid="project-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  filter === f
                    ? "border-ember bg-ember text-ink"
                    : "border-line text-ash hover:border-ember hover:text-ember"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid gap-px border border-line bg-line md:grid-cols-2" data-testid="project-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.name}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-ink p-8 transition-colors duration-500 hover:bg-coal md:p-12"
                data-testid={`project-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-sm text-ember">{String(PROJECTS.indexOf(p) + 1).padStart(2, "0")}</span>
                  <span className="border border-line px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-ash">
                    {p.category}
                  </span>
                </div>
                <h2 className="mt-14 font-display text-2xl font-semibold uppercase leading-tight tracking-tight text-bone md:text-3xl">
                  {p.name}
                </h2>
                <p className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                  <MapPin size={12} className="text-ember" />
                  {p.location}
                </p>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-ember transition-[width] duration-500 group-hover:w-full" aria-hidden="true" />
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal>
          <div className="mt-24 flex flex-col items-start gap-8 border border-line bg-coal p-10 md:flex-row md:items-center md:justify-between md:p-16">
            <h2 className="font-display text-3xl font-bold uppercase tracking-tighter text-bone md:text-4xl">
              Ihr Bauvorhaben<br />als Nummer 17?
            </h2>
            <Link
              to="/kontakt"
              data-testid="projekte-cta-button"
              className="group inline-flex items-center gap-3 bg-ember px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bone"
            >
              Gespräch vereinbaren
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      <Marquee reverse outline={false} />
    </main>
  );
}
